import { getApiBase } from './runtimeConfig';

const API_BASE = getApiBase();

// ─── Auth header helper (admin only) ──────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem('admin_token');
  return token
    ? { Authorization: `Bearer ${token}` }
    : {};
}

// ─── Response helper ──────────────────────────────────────────────────────────

async function parseResponse(res) {
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

function uploadWithProgress(url, {
  method = 'POST',
  body,
  headers = {},
  onProgress,
} = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);

    Object.entries(headers).forEach(([key, value]) => {
      if (value != null) xhr.setRequestHeader(key, value);
    });

    if (typeof onProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
        onProgress(pct);
      };
    }

    xhr.onerror = () => reject(new Error('Network error'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.onload = () => {
      let data;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (_) {
        data = {};
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
      } else {
        reject(new Error(data.error || `HTTP ${xhr.status}`));
      }
    };

    xhr.send(body);
  });
}

// ─── Public: Posts ────────────────────────────────────────────────────────────

export async function getPosts(page = 1, limit = 20, sort = 'latest', country = '', place = '') {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sort,
  });
  if (country) query.set('country', country);
  if (place) query.set('place', place);
  const res = await fetch(`${API_BASE}/posts?${query.toString()}`);
  return parseResponse(res);
}

export async function getPostById(postId) {
  const res = await fetch(`${API_BASE}/posts/${postId}`);
  return parseResponse(res);
}

/**
 * @param {FormData} formData  Must include `content` field and optionally `media` files.
 */
export async function createPost(formData, options = {}) {
  if (typeof options.onProgress === 'function') {
    return uploadWithProgress(`${API_BASE}/posts`, {
      method: 'POST',
      body: formData,
      onProgress: options.onProgress,
    });
  }

  // Do NOT set Content-Type — let the browser set multipart boundary automatically
  const res = await fetch(`${API_BASE}/posts`, {
    method: 'POST',
    body: formData,
  });
  return parseResponse(res);
}

// ─── Public: Comments ─────────────────────────────────────────────────────────

export async function getComments(postId, sort = 'oldest') {
  const query = new URLSearchParams({ sort });
  const res = await fetch(`${API_BASE}/posts/${postId}/comments?${query.toString()}`);
  return parseResponse(res);
}

export async function likePost(postId) {
  const res = await fetch(`${API_BASE}/posts/${postId}/like`, { method: 'POST' });
  return parseResponse(res);
}

export async function reportPost(postId, reason) {
  const endpoints = [
    `${API_BASE}/posts/${postId}/report`,
    `${API_BASE}/posts/report/${postId}`,
  ];

  let lastError = null;

  for (const endpoint of endpoints) {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });

    if (res.status === 404) {
      lastError = new Error('Route not found');
      continue;
    }

    return parseResponse(res);
  }

  throw lastError || new Error('Failed to submit report');
}

export async function createComment(postId, content, files = [], country = '', options = {}) {
  const fd = new FormData();
  fd.append('content', content);
  if (country) fd.append('country', country);
  files.forEach((f) => fd.append('media', f));

  if (typeof options.onProgress === 'function') {
    return uploadWithProgress(`${API_BASE}/posts/${postId}/comments`, {
      method: 'POST',
      body: fd,
      onProgress: options.onProgress,
    });
  }

  const res = await fetch(`${API_BASE}/posts/${postId}/comments`, {
    method: 'POST',
    body: fd,
  });
  return parseResponse(res);
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export async function adminLogin(username, password) {
  const res = await fetch(`${API_BASE}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return parseResponse(res);
}

export async function adminSetup(username, password) {
  const res = await fetch(`${API_BASE}/admin/setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return parseResponse(res);
}

export async function adminGetPosts(page = 1) {
  const res = await fetch(`${API_BASE}/admin/posts?page=${page}&limit=50`, {
    headers: authHeaders(),
  });
  return parseResponse(res);
}

export async function adminGetPostComments(postId) {
  const res = await fetch(`${API_BASE}/admin/posts/${postId}/comments`, {
    headers: authHeaders(),
  });
  return parseResponse(res);
}

export async function adminDeletePost(id) {
  const res = await fetch(`${API_BASE}/admin/posts/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return parseResponse(res);
}

export async function adminDeleteComment(id) {
  const res = await fetch(`${API_BASE}/admin/comments/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return parseResponse(res);
}

export async function adminFlagPost(id) {
  const res = await fetch(`${API_BASE}/admin/posts/${id}/flag`, {
    method: 'PUT',
    headers: authHeaders(),
  });
  return parseResponse(res);
}

export async function adminConfirmPost(id) {
  const res = await fetch(`${API_BASE}/admin/posts/${id}/confirm`, {
    method: 'PUT',
    headers: authHeaders(),
  });
  return parseResponse(res);
}

// ─── Public: Ads ──────────────────────────────────────────────────────────────

export async function getRandomAd() {
  const res = await fetch(`${API_BASE}/ads/random`);
  return parseResponse(res);
}

export async function getContextualAds({ postId = '', commentId = '', text = '', adType = '' } = {}) {
  const query = new URLSearchParams();
  if (postId) query.set('postId', postId);
  if (commentId) query.set('commentId', commentId);
  if (text) query.set('text', text);
  if (adType) query.set('adType', adType);
  const res = await fetch(`${API_BASE}/ads/contextual?${query.toString()}`);
  return parseResponse(res);
}

export async function submitAdRequest(data, options = {}) {
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

  if (isFormData && typeof options.onProgress === 'function') {
    return uploadWithProgress(`${API_BASE}/ads/request`, {
      method: 'POST',
      body: data,
      onProgress: options.onProgress,
    });
  }

  const res = await fetch(`${API_BASE}/ads/request`, isFormData
    ? {
        method: 'POST',
        body: data,
      }
    : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
  return parseResponse(res);
}

export async function fetchOgData(url) {
  const res = await fetch(`${API_BASE}/og?url=${encodeURIComponent(url)}`);
  return parseResponse(res);
}

// ─── Admin: Ads ───────────────────────────────────────────────────────────────

export async function adminGetAds(status = '') {
  const query = new URLSearchParams();
  if (status) query.set('status', status);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await fetch(`${API_BASE}/ads${suffix}`, { headers: authHeaders() });
  return parseResponse(res);
}

export async function adminReviewAdRequest(id, payload) {
  const res = await fetch(`${API_BASE}/ads/${id}/review`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function adminCreateAd(data) {
  const res = await fetch(`${API_BASE}/ads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  return parseResponse(res);
}

export async function adminToggleAd(id) {
  const res = await fetch(`${API_BASE}/ads/${id}/toggle`, {
    method: 'PUT',
    headers: authHeaders(),
  });
  return parseResponse(res);
}

export async function adminSettleAdPayment(id, settled = true) {
  const res = await fetch(`${API_BASE}/ads/${id}/settle-payment`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ settled }),
  });
  return parseResponse(res);
}

export async function adminDeleteAd(id) {
  const res = await fetch(`${API_BASE}/ads/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return parseResponse(res);
}
